import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { PermissionsByModule, RoleSummary } from '../../api/roles';

interface RolePermissionsDialogProps {
  open: boolean;
  role: RoleSummary | null;
  permissionsByModule: PermissionsByModule;
  onClose: () => void;
  onSave: (roleId: number, permissionIds: number[]) => Promise<void>;
}

export const RolePermissionsDialog = ({
  open,
  role,
  permissionsByModule,
  onClose,
  onSave,
}: RolePermissionsDialogProps) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role) {
      setSelected(new Set(role.permissions.map((p) => p.id)));
    }
  }, [role]);

  if (!role) return null;

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleModule = (moduleIds: number[], allSelected: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      moduleIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(role.id, Array.from(selected));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Permissions — {role.name}</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 480 }}>
        <Stack spacing={2}>
          {Object.entries(permissionsByModule).map(([module, permissions]) => {
            const moduleIds = permissions.map((p) => p.id);
            const allSelected = moduleIds.every((id) => selected.has(id));
            return (
              <Stack key={module}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && moduleIds.some((id) => selected.has(id))}
                      onChange={() => toggleModule(moduleIds, allSelected)}
                      disabled={role.isSystem && role.name === 'Super Admin'}
                    />
                  }
                  label={<Typography fontWeight={600}>{module}</Typography>}
                />
                <Stack direction="row" flexWrap="wrap" sx={{ pl: 4 }}>
                  {permissions.map((permission) => (
                    <FormControlLabel
                      key={permission.id}
                      sx={{ width: 160 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.has(permission.id)}
                          onChange={() => toggle(permission.id)}
                          disabled={role.isSystem && role.name === 'Super Admin'}
                        />
                      }
                      label={permission.action}
                    />
                  ))}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || (role.isSystem && role.name === 'Super Admin')}
        >
          {saving ? 'Saving…' : 'Save permissions'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
